import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { useEffect, useState } from 'react';

import { Info } from 'lucide-react';

import { useGet } from 'hooks/use-get';
import { useParams } from 'react-router-dom';
import { getConsumptionRateOptions } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/get-ofd-settings';
import DailyConsumptionPrognosisSlider from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/daily-consumption-prognosis-slider';
import DailyConsumptionRecommendedSlider from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/daily-consumption-recommended-slider';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { ConsumptionAdjustmentMethod } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/consumption-adjustment-method.enum';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';

type Props = {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.dailyConsumptionRateTab';

const EditDailyConsumption: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();

  const {
    stateHandlers: { setDailyConsumptionInput: setDailyConsumption },
    getOfdSettingsQuery: { data: ofdSettings },
  } = useInputSettingsState();

  const params = useParams<OFD2Params>();

  const currentConsumptionRate = ofdSettings?.currentConsumptionRate ?? 0;
  const originalConsumptionRate = ofdSettings?.originalConsumptionRate ?? 0;
  const consumptionRateAdjustmentDays = ofdSettings?.consumptionRateAdjustmentDays ?? 0;
  const manualConsumptionRateCoefficient = ofdSettings?.manualConsumptionRateCoefficient ?? null;
  const consumptionRateAdjustmentMethod = ofdSettings?.consumptionRateAdjustmentMethod ?? ConsumptionAdjustmentMethod.historicalAverage;
  const [isStarvationIgnored, setIsStarvationIgnored] = useState(ofdSettings?.consumptionRateAdjustmentMethod === ConsumptionAdjustmentMethod.historicalAverageIgnoreStarvation);

  const { data: consumptionRateOptions, isLoading: consumptionRateOptionsIsLoading } = useGet(
    ['getConsumptionRateOptions', params.globalSiteId, isStarvationIgnored],
    ({ queryKey }) => getConsumptionRateOptions(String(queryKey[1]), isStarvationIgnored),
    {
      enabled: !!params.globalSiteId,
    }
  );

  const [consumptionAdjustmentMethod, setConsumptionAdjustmentMethod] =
    useState<ConsumptionAdjustmentMethod>(consumptionRateAdjustmentMethod);

  // #region STATES
  const [consumptionRateDays, setConsumptionRateDays] = useState(consumptionRateAdjustmentDays || 0);
  const [dailyConsumptionPrognosis, setDailyConsumptionPrognosis] = useState(manualConsumptionRateCoefficient || 1);
  // #endregion STATES

  useEffect(() => {
    if (consumptionAdjustmentMethod === ConsumptionAdjustmentMethod.manualCoefficient) {
      setDailyConsumption({
        consumptionRateAdjustmentDays: consumptionRateDays,
        manualConsumptionRateCoefficient: dailyConsumptionPrognosis,
        consumptionRateAdjustmentMethod: consumptionAdjustmentMethod,
      });
    } else {
      setDailyConsumption({
        consumptionRateAdjustmentDays: consumptionRateDays,
        manualConsumptionRateCoefficient: null,
        consumptionRateAdjustmentMethod: consumptionAdjustmentMethod,
      });
    }
  }, [dailyConsumptionPrognosis, consumptionRateDays, consumptionAdjustmentMethod]);

  useEffect(() => {
    setIsStarvationIgnored(consumptionAdjustmentMethod === ConsumptionAdjustmentMethod.historicalAverageIgnoreStarvation);
  }, [consumptionAdjustmentMethod]);

  if (!ofdSettings) return null;

  // #endregion

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box display={'flex'} alignItems={'center'}>
          <Box display={'flex'} gap={2} alignItems={'center'}>
            <Typography variant="h2" fontWeight={800}>
              <FormattedNumber
                value={Number(currentConsumptionRate)}
                unit={'kilogram'}
                style="unit"
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
            </Typography>
            <FormattedMessage id={`${PAGE_NAME_ID}.day`} />
          </Box>
          <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltipMethod` })}>
            <IconButton>
              <Info size={18} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <FormattedMessage id={`${PAGE_NAME_ID}.calculationMethod`} />
          {consumptionAdjustmentMethod === ConsumptionAdjustmentMethod.historicalAverage
            ? intl.formatMessage({ id: `${PAGE_NAME_ID}.editRecommended.historicalAverage` })
            : intl.formatMessage({ id: `${PAGE_NAME_ID}.editPrognosis.prognosis` })}
        </Box>
      </Box>
      <Divider sx={{ m: theme.spacing(2, 0, 4, 0) }} />

      {ofdSettings?.consumptionRateAdjustmentMethod === ConsumptionAdjustmentMethod.manualCoefficient && (
        <Alert severity="info">
          <FormattedMessage id={`${PAGE_NAME_ID}.editPrognosis.alert`} />
        </Alert>
      )}

      <Typography>
        <FormattedMessage id={`${PAGE_NAME_ID}.selectedCalculationLabel`} />:
      </Typography>
      <FormControl variant={'standard'}>
        <RadioGroup
          value={consumptionAdjustmentMethod}
          onChange={(event, value) => setConsumptionAdjustmentMethod(value as ConsumptionAdjustmentMethod)}>
          <FormControlLabel
            value={ConsumptionAdjustmentMethod.historicalAverage}
            control={<Radio />}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.recommended` })}
          />
          <FormControlLabel
            value={ConsumptionAdjustmentMethod.historicalAverageIgnoreStarvation}
            control={<Radio />}
            label={
              <Box display={'flex'} gap={1} alignItems={'center'}>
                <Typography component={'span'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.sealice` })}</Typography>
                <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.sealiceTooltip` })}>
                  <IconButton>
                    <Info size={18} />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
          <FormControlLabel
            value={ConsumptionAdjustmentMethod.manualCoefficient}
            control={<Radio />}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.manual` })}
          />

        </RadioGroup>
      </FormControl>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} p={4} sx={{ background: '#F7F8F8' }}>
        <Typography>
          <FormattedMessage id={`${PAGE_NAME_ID}.prognosis`} />
          :&nbsp;
          {originalConsumptionRate != null ? (
            <FormattedNumber
              value={Number(originalConsumptionRate)}
              unit={'kilogram'}
              style="unit"
              minimumFractionDigits={0}
              maximumFractionDigits={0}
            />
          ) : (
            <FormattedMessage id={`general.notApplicable`} />
          )}


        </Typography>
        <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltipInfo` })}>
          <IconButton>
            <Info size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      {(consumptionAdjustmentMethod === ConsumptionAdjustmentMethod.historicalAverage || consumptionAdjustmentMethod === ConsumptionAdjustmentMethod.historicalAverageIgnoreStarvation)
        ? intl.formatMessage({ id: `${PAGE_NAME_ID}.editRecommended.sliderHeader` })
        : intl.formatMessage({ id: `${PAGE_NAME_ID}.editPrognosis.sliderHeader` })}

      {(consumptionAdjustmentMethod === ConsumptionAdjustmentMethod.historicalAverage
        || consumptionAdjustmentMethod === ConsumptionAdjustmentMethod.historicalAverageIgnoreStarvation) ? (
        <DailyConsumptionRecommendedSlider
          setConsumptionRateDays={setConsumptionRateDays}
          consumptionRateOptions={consumptionRateOptions}
          isLoading={consumptionRateOptionsIsLoading}
        />
      ) : (
        <DailyConsumptionPrognosisSlider setDailyConsumptionPrognosis={setDailyConsumptionPrognosis} />
      )}
    </Box>
  );
};

export default EditDailyConsumption;
